class TczVariantSelector extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    // Prevent double initialization
    if (this.initialized) return;
    this.initialized = true;

    // Small delay to ensure DOM is fully ready for dynamically loaded content
    setTimeout(() => {
      this.init();
    }, 0);
  }

  init() {
    this.miniCart = document.querySelector('mini-cart');
    this.mobileSheet = this.querySelector('.tcz-mobile-sheet');
    this.quickAddButton = this.querySelector('.tcz-quick-add-btn');
    
    // Desktop variant buttons (query before moving mobile sheet)
    this.desktopButtons = this.querySelectorAll('.tcz-variants-desktop .tcz-variant-btn:not([disabled])');
    this.desktopButtons.forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
    
    // Mobile sheet setup
    if (this.mobileSheet) {
      // Store reference to this element in the mobile sheet for later cleanup
      this.mobileSheet.tczVariantSelector = this;
      
      // Query mobile buttons BEFORE moving the sheet
      this.mobileButtons = this.mobileSheet.querySelectorAll('.tcz-mobile-variant-btn:not([disabled])');
      this.mobileButtons.forEach((button) => {
        button.addEventListener('click', this.onMobileButtonClick.bind(this));
      });
      
      // Close handlers
      const closeButton = this.mobileSheet.querySelector('.tcz-mobile-close');
      const backdrop = this.mobileSheet.querySelector('.tcz-mobile-backdrop');
      
      if (closeButton) {
        closeButton.addEventListener('click', this.closeMobileSheet.bind(this));
      }
      
      if (backdrop) {
        backdrop.addEventListener('click', this.closeMobileSheet.bind(this));
      }
      
      // Move mobile sheet to body to avoid positioning issues (do this LAST)
      document.body.appendChild(this.mobileSheet);
    }
    
    // Mobile quick add button
    if (this.quickAddButton) {
      this.quickAddButton.addEventListener('click', this.openMobileSheet.bind(this));
    }
  }

  disconnectedCallback() {
    // Clean up mobile sheet when element is removed
    if (this.mobileSheet && this.mobileSheet.parentNode === document.body) {
      document.body.removeChild(this.mobileSheet);
    }
  }
  
  openMobileSheet() {
    if (this.mobileSheet) {
      // Store current scroll position
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      
      // Add classes and styles
      this.mobileSheet.classList.add('active');
      document.body.classList.add('tcz-sheet-open');
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.overflow = 'hidden';
    }
  }
  
  closeMobileSheet() {
    if (this.mobileSheet) {
      // Remove classes and styles
      this.mobileSheet.classList.remove('active');
      document.body.classList.remove('tcz-sheet-open');
      document.body.style.overflow = '';
      document.body.style.top = '';
      
      // Restore scroll position
      if (this.scrollPosition !== undefined) {
        window.scrollTo(0, this.scrollPosition);
        this.scrollPosition = undefined;
      }
    }
  }
  
  onMobileButtonClick(event) {
    this.onButtonClick(event);
    // Close sheet after adding to cart
    setTimeout(() => {
      this.closeMobileSheet();
    }, 800);
  }

  onButtonClick(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    
    if (!variantId || button.classList.contains('loading')) return;

    // Add to cart
    this.addToCart(button, variantId);
  }

  addToCart(button, variantId) {
    // If on cart page or cart drawer is disabled, use standard form submission
    if (document.body.classList.contains('template-cart') || !theme.shopSettings.cartDrawer) {
      Shopify.postLink(theme.routes.cart_add_url, {
        parameters: {
          id: variantId,
          quantity: 1
        },
      });
      return;
    }

    // Disable button and show loading state
    button.setAttribute('disabled', '');
    button.classList.add('loading');

    // Get sections to update
    const sections = this.miniCart ? this.miniCart.getSectionsToRender().map((section) => section.id) : [];

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: sections,
      sections_url: window.location.pathname
    });

    fetch(`${theme.routes.cart_add_url}`, { 
      ...fetchConfig('javascript'), 
      body 
    })
      .then((response) => response.json())
      .then((parsedState) => {
        // Update mini cart
        if (this.miniCart) {
          this.miniCart.renderContents(parsedState);
        }
        
        // Show success feedback
        this.showSuccessFeedback(button);
      })
      .catch((e) => {
        console.error('Error adding to cart:', e);
        // Show error feedback
        this.showErrorFeedback(button);
      })
      .finally(() => {
        // Re-enable button after a short delay
        setTimeout(() => {
          button.removeAttribute('disabled');
          button.classList.remove('loading');
        }, 1000);
      });
  }

  showSuccessFeedback(button) {
    const originalText = button.textContent;
    button.textContent = '✓';
    button.style.backgroundColor = '#000';
    button.style.color = '#fff';
    
    setTimeout(() => {
      button.textContent = originalText;
      button.style.backgroundColor = '';
      button.style.color = '';
    }, 800);
  }

  showErrorFeedback(button) {
    const originalText = button.textContent;
    button.textContent = '✕';
    button.style.backgroundColor = '#ff0000';
    button.style.color = '#fff';
    
    setTimeout(() => {
      button.textContent = originalText;
      button.style.backgroundColor = '';
      button.style.color = '';
    }, 800);
  }
}

customElements.define('tcz-variant-selector', TczVariantSelector);

// Simple Add to Cart handler for products without sizes
document.addEventListener('DOMContentLoaded', () => {
  const simpleAtcButtons = document.querySelectorAll('.tcz-simple-atc-btn');
  
  simpleAtcButtons.forEach(button => {
    button.addEventListener('click', function() {
      const variantId = this.dataset.variantId;
      const miniCart = document.querySelector('mini-cart');
      
      if (!variantId || this.classList.contains('loading')) return;
      
      // If on cart page or cart drawer is disabled, use standard form submission
      if (document.body.classList.contains('template-cart') || !theme.shopSettings.cartDrawer) {
        Shopify.postLink(theme.routes.cart_add_url, {
          parameters: {
            id: variantId,
            quantity: 1
          },
        });
        return;
      }
      
      // Add loading state
      this.setAttribute('disabled', '');
      this.classList.add('loading');
      
      const sections = miniCart ? miniCart.getSectionsToRender().map((section) => section.id) : [];
      const body = JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sections,
        sections_url: window.location.pathname
      });
      
      fetch(`${theme.routes.cart_add_url}`, { 
        ...fetchConfig('javascript'), 
        body 
      })
        .then((response) => response.json())
        .then((parsedState) => {
          if (miniCart) {
            miniCart.renderContents(parsedState);
          }
          
          // Show success feedback
          const originalText = this.textContent;
          this.textContent = '✓ ADDED';
          this.style.backgroundColor = '#000';
          
          setTimeout(() => {
            this.textContent = originalText;
            this.style.backgroundColor = '';
          }, 1500);
        })
        .catch((e) => {
          console.error('Error adding to cart:', e);
          
          // Show error feedback
          const originalText = this.textContent;
          this.textContent = '✕ ERROR';
          this.style.backgroundColor = '#ff0000';
          
          setTimeout(() => {
            this.textContent = originalText;
            this.style.backgroundColor = '';
          }, 1500);
        })
        .finally(() => {
          setTimeout(() => {
            this.removeAttribute('disabled');
            this.classList.remove('loading');
          }, 1500);
        });
    });
  });
});
